import React, { useState, useEffect, useCallback } from 'react';
import Button from '../../../components/BaseComponents/Button';
import ConditionsTable from './ConditionsTable';
import ConditionsModal from './ConditionViewModal';
import ViewConditionModal from './ConditionViewModal/ViewConditionModal';
import DeleteConditionModal from './ConditionViewModal/DeleteConditionModal';
import { getConditionsList, ConditionWithDetails } from '../../../api/conditions';
import './ConditionsTab.scss';

const Conditions = () => {
  const [conditionsList, setConditionsList] = useState<ConditionWithDetails[]>([]);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editingCondition, setEditingCondition] = useState<ConditionWithDetails | null>(null);
  const [viewingCondition, setViewingCondition] = useState<ConditionWithDetails | null>(null);
  const [deletingCondition, setDeletingCondition] = useState<ConditionWithDetails | null>(null);
  const [searchQuery] = useState('');
  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [sortingLoading, setSortingLoading] = useState<boolean>(false);

  const fetchConditions = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getConditionsList(searchQuery, 50, 0, sortField, sortOrder);
      setConditionsList(response.conditions);
    } catch (error) {
      console.error('Error fetching conditions:', error);
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, sortField, sortOrder]);

  // Fetch conditions on component mount and when search/sort changes
  useEffect(() => {
    fetchConditions();
  }, [fetchConditions]);

  const onAddConditionBtnClick = () => {
    setEditingCondition(null);
    setIsFormModalOpen(true);
  };

  const onViewCondition = (condition: ConditionWithDetails) => {
    setViewingCondition(condition);
    setIsViewModalOpen(true);
  };

  const onEditCondition = (condition: ConditionWithDetails) => {
    setEditingCondition(condition);
    setIsFormModalOpen(true);
  };

  const onCloseFormModal = () => {
    setIsFormModalOpen(false);
    setEditingCondition(null);
  };

  const onCloseViewModal = () => {
    setIsViewModalOpen(false);
    setViewingCondition(null);
  };

  const onCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeletingCondition(null);
  };

  const onConditionSaved = async () => {
    await fetchConditions(); // Refresh the list
    setIsFormModalOpen(false);
    setEditingCondition(null);
  };

  const onDeleteCondition = (conditionId: number) => {
    const condition = conditionsList.find(c => c.id === conditionId);
    if (condition) {
      setDeletingCondition(condition);
      setIsDeleteModalOpen(true);
    }
  };

  const onConditionDeleted = async () => {
    await fetchConditions(); // Refresh the list
    setIsDeleteModalOpen(false);
    setIsViewModalOpen(false); // Close the view modal as well
    setIsFormModalOpen(false); // Close the edit modal as well
    setDeletingCondition(null);
    setViewingCondition(null);
    setEditingCondition(null);
  };

  const handleSort = async (field: string) => {
    let newOrder: 'asc' | 'desc' = 'asc';
    if (sortField === field && sortOrder === 'asc') {
      newOrder = 'desc';
    }
    
    setSortField(field);
    setSortOrder(newOrder);
    
    // Add sorting loader with delay for better UX
    setSortingLoading(true);
    await new Promise(resolve => setTimeout(resolve, 300));
    setSortingLoading(false);
  };

  return (
    <>
      <div className='search-row-container'>
        <div className='search-and-filter-container'>
          <div className='count-container'>
          Medical {conditionsList.length === 1 ? 'condition' : 'conditions'} ({conditionsList.length})
          </div>
        </div>
        <Button
          isSecondary={true}
          text='Add Medical condition'
          onClick={onAddConditionBtnClick}
        />
      </div>

      <div className='conditions-container'>
        <ConditionsTable 
          conditionsList={conditionsList} 
          isLoading={isLoading}
          sortingLoading={sortingLoading}
          onViewCondition={onViewCondition}
          onEditCondition={onEditCondition}
          onDeleteCondition={onDeleteCondition}
          onSort={handleSort}
        />
      </div>

      {isFormModalOpen && (
        <ConditionsModal
          isOpen={isFormModalOpen}
          onClose={onCloseFormModal}
          conditionToEdit={editingCondition}
          onConditionSaved={onConditionSaved}
          onDeleteCondition={onDeleteCondition}
        />
      )}

      {isViewModalOpen && (
        <ViewConditionModal
          isOpen={isViewModalOpen}
          onClose={onCloseViewModal}
          condition={viewingCondition}
          onEdit={onEditCondition}
          onDelete={onDeleteCondition}
        />
      )}

      {isDeleteModalOpen && (
        <DeleteConditionModal
          isOpen={isDeleteModalOpen}
          onClose={onCloseDeleteModal}
          conditionToDelete={deletingCondition}
          onConditionDeleted={onConditionDeleted}
        />
      )}
    </>
  );
};

export default Conditions;
